/**
 * DashboardScreen — Matrice d'Eisenhower.
 * À l'ajout manuel d'une tâche, délègue à AIService.scorePriority pour
 * obtenir automatiquement un score IA et (si souhaité) un quadrant suggéré.
 */
import { useState } from 'react';
import type { JSX } from 'react';
import { EisenhowerMatrix } from '@modules/taskMatrix/EisenhowerMatrix';
import { useUserStore } from '@store/userStore';
import { useTaskStore } from '@store/taskStore';
import { useNavStore } from '@store/navStore';
import { formatFrenchLongDate } from '@utils/dateUtils';
import type { NewTaskInput, Quadrant } from '@store/types';
import { AIService, AIServiceError } from '@services/AIService';

export default function DashboardScreen(): JSX.Element {
  const profile = useUserStore((s) => s.profile);
  const addTask = useTaskStore((s) => s.addTask);
  const updateTask = useTaskStore((s) => s.updateTask);
  const startFocus = useNavStore((s) => s.startFocus);
  const [aiNotice, setAiNotice] = useState<string | null>(null);

  const handleAdd = async (quadrant: Quadrant) => {
    const title = window.prompt('Titre de la nouvelle tâche :');
    if (!title) return;

    const input: NewTaskInput = {
      title,
      quadrant,
      source: 'manual',
      dueAt: null,
      estimatedMinutes: 30,
    };
    const created = addTask(input);

    // Scoring IA en tâche de fond — n'interrompt pas l'UX
    try {
      const result = await AIService.scorePriority({
        title,
        source: 'manual',
        due_in_hours: null,
        current_hour: new Date().getHours(),
      });
      updateTask(created.id, {
        aiScore: result.score,
        quadrant: result.quadrant,
      });
      setAiNotice(`IA : score ${result.score} → ${result.quadrant}`);
      window.setTimeout(() => setAiNotice(null), 4000);
    } catch (err) {
      if (err instanceof AIServiceError) {
        setAiNotice('Backend IA indisponible — score par défaut conservé.');
        window.setTimeout(() => setAiNotice(null), 4000);
      }
    }
  };

  const firstName = profile?.firstName ?? 'Utilisateur';

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1>Bonjour {firstName} ✦</h1>
          <p className="mt-2 text-sm text-[color:var(--color-text-medium)] capitalize">
            {formatFrenchLongDate()} — votre cockpit du jour
          </p>
        </div>
        {aiNotice && (
          <div className="unique-toast-success text-xs font-semibold animate-slide-up">
            {aiNotice}
          </div>
        )}
      </div>

      <EisenhowerMatrix onAddTask={handleAdd} onStartTask={startFocus} />
    </div>
  );
}
